import { Link } from "react-router-dom";
import { ShoppingCart, PartyPopper } from "lucide-react";
import useAppStore from "../store/useAppStore";

function CookieBanner() {
  const { cookieConsent, setCookieConsent } = useAppStore();
  if (cookieConsent !== null) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-4 border-black p-4">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-sm text-black font-body">
          We use cookies to save your preferences. No tracking.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setCookieConsent(true)}
            className="px-5 py-2 bg-brand-primary text-black font-label font-extrabold text-sm uppercase tracking-wide border-4 border-black rounded-block block-shadow-1 btn-press"
          >
            Accept
          </button>
          <button
            onClick={() => setCookieConsent(false)}
            className="px-5 py-2 bg-white text-black font-label font-extrabold text-sm uppercase tracking-wide border-4 border-black rounded-block btn-press"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { cart, cookieConsent } = useAppStore();
  const cartCount = cart.length;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:rounded-block focus:font-label focus:font-extrabold focus:text-sm focus:uppercase focus:bg-brand-primary focus:text-black focus:border-4 focus:border-black"
      >
        Skip to main content
      </a>
      <div className={`min-h-screen bg-brand-surface flex flex-col font-body${cookieConsent === null ? " pb-24" : ""}`}>
        <nav className="bg-brand-primary sticky top-0 z-40 border-b-4 border-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-black">
              <span className="inline-flex items-center justify-center w-10 h-10 bg-white border-4 border-black rounded-block">
                <PartyPopper className="w-5 h-5 text-black" strokeWidth={2.5} />
              </span>
              <span className="font-heading font-extrabold text-2xl tracking-tight">PrintPartyHub</span>
            </Link>

            <Link
              to="/checkout"
              aria-label={`View cart${cartCount ? ` (${cartCount} item${cartCount === 1 ? "" : "s"})` : ""}`}
              className="relative inline-flex items-center justify-center w-11 h-11 bg-white border-4 border-black rounded-block block-shadow-1 btn-press"
            >
              <ShoppingCart className="w-5 h-5 text-black" strokeWidth={2.5} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 bg-brand-secondary text-white text-xs font-label font-extrabold border-2 border-black rounded-full flex items-center justify-center">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>
          </div>
        </nav>

        <main id="main-content" className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </main>

        <footer className="bg-white border-t-4 border-black py-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-4">
            <span className="font-heading font-extrabold text-2xl text-black">
              PrintPartyHub
            </span>
            <div className="flex flex-wrap items-center justify-center gap-5 text-sm font-label font-extrabold uppercase tracking-wide">
              <Link to="/faq" className="text-black hover:underline">FAQ</Link>
              <Link to="/contact" className="text-black hover:underline">Contact</Link>
              <Link to="/about" className="text-black hover:underline">About</Link>
              <Link to="/terms" className="text-black hover:underline">Terms</Link>
              <Link to="/privacy" className="text-black hover:underline">Privacy</Link>
            </div>
            <p className="text-xs text-black font-body">
              Questions? Email{" "}
              <a href="mailto:support@printpartyhub.com" className="underline font-bold">
                support@printpartyhub.com
              </a>
            </p>
            <p className="text-xs text-black font-body opacity-70">
              © {new Date().getFullYear()} Trellis Digital Services LLC
            </p>
          </div>
        </footer>

        <CookieBanner />
      </div>
    </>
  );
}
